import { zCurrency } from "@redotech/money/usd-amounts";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { OutboundShipmentTaxIdSchema } from "./customs";
import { LandedCostProvider, LandedCostSource } from "./landed-cost-provider";

const zonosProviderInfoSchema = z
  .object({
    provider: z
      .literal(LandedCostProvider.ZONOS)
      .meta({ privacy: privacy.enumOrStatus }),
    quoteId: z.string().meta({ privacy: privacy.id.external.resource }),
    orderId: z
      .string()
      .meta({ privacy: privacy.id.external.resource })
      .nullish(),
    merchantAccountId: z
      .string()
      .meta({ privacy: privacy.id.external.resource })
      .nullish(),
  })
  .meta({ privacy: privacy.structured });
export const ZonosProviderInfoSchema: z.ZodType<ZonosProviderInfo> =
  zonosProviderInfoSchema;
export type ZonosProviderInfo = z.infer<typeof zonosProviderInfoSchema>;

const landedCostProviderInfoSchema = z
  .discriminatedUnion("provider", [
    zonosProviderInfoSchema.meta({ privacy: privacy.structured }),
  ])
  .meta({ privacy: privacy.structured });
export const LandedCostProviderInfoSchema: z.ZodType<LandedCostProviderInfo> =
  landedCostProviderInfoSchema;
export type LandedCostProviderInfo = z.infer<
  typeof landedCostProviderInfoSchema
>;

const rawLandedCostTaxIdentifierSchema = z
  .object({
    type: z.string().meta({ privacy: privacy.enumOrStatus }),
    value: z.string().meta({ privacy: privacy.id.government }),
  })
  .meta({ privacy: privacy.structured });
export const RawLandedCostTaxIdentifierSchema: z.ZodType<RawLandedCostTaxIdentifier> =
  rawLandedCostTaxIdentifierSchema;
export type RawLandedCostTaxIdentifier = z.infer<
  typeof rawLandedCostTaxIdentifierSchema
>;

/** Mirrors `LandedCostFeeBreakdown` in logistics/landed-cost/src/landed-cost-provider.ts; duplicated to avoid a dependency cycle. */
const landedCostFeeBreakdownSchema = z
  .object({
    guaranteeOrder: z.number().meta({ privacy: privacy.money }),
    guaranteePercent: z.number().meta({ privacy: privacy.money }),
    otherFees: z.number().meta({ privacy: privacy.money }),
    revenue: z.number().meta({ privacy: privacy.money }),
  })
  .meta({ privacy: privacy.structured });
export type LandedCostFeeBreakdown = z.infer<
  typeof landedCostFeeBreakdownSchema
>;

// Redo profit/spread is the redoPerOrderFee - providerOrderFee
export const rateLandedCostSchema = z
  .object({
    providerInfo: LandedCostProviderInfoSchema.meta({
      privacy: privacy.structured,
    }),
    duties: z.number().meta({ privacy: privacy.money }), // minor units - the actual landed cost duties that the merchant gets charged
    taxes: z.number().meta({ privacy: privacy.money }), // minor units - the actual landed cost taxes that the merchant gets charged
    fees: z.number().meta({ privacy: privacy.money }), // minor units - the actual landed cost fees that the merchant gets charged
    providerOrderFee: z.number().meta({ privacy: privacy.money }), // minor units — the provider's per-order fee
    redoPerOrderFee: z.number().meta({ privacy: privacy.money }), // minor units — Redo's per-order fee
    drawbackCoverageFee: z.number().meta({ privacy: privacy.money }).nullish(),
    feeBreakdown: landedCostFeeBreakdownSchema
      .optional()
      .meta({ privacy: privacy.structured }),
    currency: zCurrency.meta({ privacy: privacy.money }),
    /**
     * Zonos guarantee code for the quote this landed cost came from (e.g.
     * "POSTAL_DDP", "ZONOS", "INCLUSIVE_PRICE"). Stored as a plain string to
     * avoid coupling the model to the Zonos client enum. Read at lock time to
     * decide whether a third-party duty/tax billing account is required. Nullish
     * for legacy entries written before this field existed.
     */
    landedCostGuaranteeCode: z
      .string()
      .meta({ privacy: privacy.enumOrStatus })
      .nullish(),
  })
  .meta({ privacy: privacy.structured });

export type RateLandedCost = z.infer<typeof rateLandedCostSchema>;

export function isMerchantOwnedLandedCost(
  landedCost: Pick<RateLandedCost, "providerInfo">,
): boolean {
  return !!landedCost.providerInfo.merchantAccountId;
}

/**
 * Merchant billing snapshot for a committed LCG. Populated when the commit
 * is subject to usage-based billing (currently the outside-OMS / UPS Unlock
 * flow). Held alongside the lock so the audit-safe per-commit fee survives
 * later team-settings changes.
 *
 * - `feeCents` — audit-safe snapshot of `team.settings.landedCost.landedCostFee`
 *   at commit time
 * - `billedAt` — stamped when the commit is included in an invoice
 * - `refundPending` — set true when an already-billed commit is cancelled
 * - `refundedAt` — stamped when the credit line item is emitted
 */
const landedCostBillingSchema = z
  .object({
    feeCents: z.number().meta({ privacy: privacy.money }).nullish(),
    drawbackCoverageCents: z
      .number()
      .meta({ privacy: privacy.money })
      .nullish(),
    billedAt: z.date().meta({ privacy: privacy.timestamp }).nullish(),
    refundPending: z.boolean().meta({ privacy: privacy.flag }).nullish(),
    refundedAt: z.date().meta({ privacy: privacy.timestamp }).nullish(),
  })
  .meta({ privacy: privacy.structured });
export type LandedCostBilling = z.infer<typeof landedCostBillingSchema>;

export const LockedLandedCostSchema = rateLandedCostSchema.extend({
  taxIdentifiers: z
    .array(OutboundShipmentTaxIdSchema)
    .meta({ privacy: privacy.structured }),
  // Absent for carrier-native-DDP carriers (ePostGlobal, APC) — see
  // `carrierHandlesDdpNatively`. `.nullish()` (not `.optional()`) so reads
  // tolerate already-stored `null` values in addition to `undefined`.
  thirdPartyBillingAccount: z
    .string()
    .meta({ privacy: privacy.credential })
    .nullish(),
  billingCountryCode: z
    .string()
    .meta({ privacy: privacy.geo.coarse })
    .nullish(),
  billingPostalCode: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
  rawResponse: z.unknown().meta({ privacy: privacy.untypedPayload }).nullish(), // Just for debugging/auditing
  billing: landedCostBillingSchema
    .nullish()
    .meta({ privacy: privacy.structured }),
  /** Commit time; window key for outside-OMS usage billing. Nullish for legacy/in-OMS entries. */
  committedAt: z.date().meta({ privacy: privacy.timestamp }).nullish(),
  /**
   * Shopify Fulfillment GID this commit corresponds to, when the commit was
   * keyed per-fulfillment (the outside-OMS / UPS Unlock flow). Lets the
   * commit/cancel activities target the right entry on a FulfillmentGroup
   * whose `fulfillments[]` array could in theory hold more than one entry,
   * without relying on the OMS upsert logic's 1:1 invariant.
   *
   * Nullish for backward compatibility with any pre-per-fulfillment-keying
   * entries (in-OMS lock flow does not populate this).
   */
  fulfillmentExternalId: z
    .string()
    .meta({ privacy: privacy.id.external.resource })
    .nullish(),
  source: createEnum(LandedCostSource, { enumName: "LandedCostSource" })
    .meta({ privacy: privacy.enumOrStatus })
    .nullish(),
});

export type LockedLandedCost = z.infer<typeof LockedLandedCostSchema>;
